/*
  Atlas contours: real iso-lines, drawn on a canvas.

  A 2-D height field is built from anisotropic Gaussian hills and hollows plus
  a low-amplitude roughness term, then marching squares extracts the levels.
  That is why these close around peaks, pinch into saddles and bunch where the
  ground is steep: they are properties of the field, not effects layered on
  afterwards. Stacked sine waves can only ever run parallel, which is what the
  sale bar used to do and why it read as stripes rather than ground.

  Seeded, so every visitor sees the same ground and a resize redraws it rather
  than reshuffling it.

  One engine, two callers with different proportions:
    - the sale bar, 44px tall, where features sit mostly outside the strip so
      what shows is a crop through much larger hills
    - the page hero, ~380px tall, where whole islands fit and read as a chart

  window.AtlasContours.draw(canvas, options)
*/
(function () {
  "use strict";

  var DEFAULTS = {
    cell: 6 /* sample spacing in px */,
    levels: 19,
    indexEvery: 5 /* every fifth line is an index contour, as on a printed chart */,
    seed: 0x5ea1e,
    density: 190 /* one feature per this many px of width */,
    spreadX: 1 /* feature sigma, as a multiple of height */,
    spreadY: 1,
    reach: 1 /* how far above and below the box feature centres sit */,
    stroke: "rgba(158, 186, 203, 0.34)",
    strokeIndex: "rgba(207, 169, 97, 0.62)",
    width: 0.85,
    widthIndex: 1.2,
  };

  /* mulberry32: small, fast, identical across browsers */
  function seeded(seed) {
    return function () {
      seed |= 0;
      seed = (seed + 0x6d2b79f5) | 0;
      var t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function buildFeatures(w, h, o) {
    var rnd = seeded(o.seed);
    var count = Math.max(5, Math.round(w / o.density));
    var out = [];
    for (var i = 0; i < count; i++) {
      var angle = rnd() * Math.PI;
      out.push({
        cx: (i + 0.35 + rnd() * 0.3) * (w / count),
        /* centres ranged above and below the box: `reach` 1 keeps whole hills
           inside a tall hero, 2.7 pushes them off a short strip */
        cy: h * (0.5 - o.reach / 2 + rnd() * o.reach),
        sx: h * o.spreadX * (0.9 + rnd() * 1.6),
        sy: h * o.spreadY * (0.55 + rnd() * 1.05),
        amp: (rnd() < 0.45 ? -1 : 1) * (0.55 + rnd() * 0.75),
        cos: Math.cos(angle),
        sin: Math.sin(angle),
      });
    }
    return out;
  }

  function sampleField(w, h, o) {
    var features = buildFeatures(w, h, o);
    var gx = Math.ceil(w / o.cell) + 1;
    var gy = Math.ceil(h / o.cell) + 1;
    var field = new Float32Array(gx * gy);
    var min = Infinity;
    var max = -Infinity;

    for (var j = 0; j < gy; j++) {
      for (var i = 0; i < gx; i++) {
        var x = i * o.cell;
        var y = j * o.cell;
        var v = 0;
        for (var k = 0; k < features.length; k++) {
          var f = features[k];
          var dx = x - f.cx;
          var dy = y - f.cy;
          /* rotate into the feature's own axes, so hills sit at an angle */
          var u = (dx * f.cos + dy * f.sin) / f.sx;
          var t = (-dx * f.sin + dy * f.cos) / f.sy;
          v += f.amp * Math.exp(-(u * u + t * t));
        }
        /* roughness, well below the hills' amplitude: it wrinkles the lines
           without inventing features of its own */
        v += 0.055 * Math.sin(x / 37 + y / 23) + 0.035 * Math.sin(x / 17 - y / 29);
        field[j * gx + i] = v;
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    return { field: field, gx: gx, gy: gy, min: min, max: max };
  }

  /* One iso-line as a batch of segments. Segments rather than stitched
     polylines: at a hairline with round caps the joins are invisible, and
     stitching would cost more than it shows. */
  function traceLevel(g, data, level, cell) {
    var field = data.field;
    var gx = data.gx;
    var gy = data.gy;

    function lerp(v0, v1) {
      var d = v1 - v0;
      return d === 0 ? 0.5 : (level - v0) / d;
    }

    g.beginPath();
    for (var j = 0; j < gy - 1; j++) {
      for (var i = 0; i < gx - 1; i++) {
        var x = i * cell;
        var y = j * cell;
        var tl = field[j * gx + i];
        var tr = field[j * gx + i + 1];
        var br = field[(j + 1) * gx + i + 1];
        var bl = field[(j + 1) * gx + i];

        var code = (tl > level ? 8 : 0) | (tr > level ? 4 : 0) | (br > level ? 2 : 0) | (bl > level ? 1 : 0);
        if (code === 0 || code === 15) continue;

        var top = [x + lerp(tl, tr) * cell, y];
        var right = [x + cell, y + lerp(tr, br) * cell];
        var bottom = [x + lerp(bl, br) * cell, y + cell];
        var left = [x, y + lerp(tl, bl) * cell];

        switch (code) {
          case 1: case 14: seg(g, left, bottom); break;
          case 2: case 13: seg(g, bottom, right); break;
          case 3: case 12: seg(g, left, right); break;
          case 4: case 11: seg(g, top, right); break;
          case 6: case 9: seg(g, top, bottom); break;
          case 7: case 8: seg(g, left, top); break;
          /* saddles: the cell genuinely holds two separate lines */
          case 5: seg(g, left, top); seg(g, bottom, right); break;
          case 10: seg(g, left, bottom); seg(g, top, right); break;
        }
      }
    }
    g.stroke();
  }

  function seg(g, a, b) {
    g.moveTo(a[0], a[1]);
    g.lineTo(b[0], b[1]);
  }

  function draw(canvas, options) {
    if (!canvas || !canvas.getContext) return;
    var o = {};
    for (var k in DEFAULTS) o[k] = DEFAULTS[k];
    for (var j in options || {}) o[j] = options[j];

    var box = canvas.getBoundingClientRect();
    var w = Math.max(1, Math.round(box.width || canvas.offsetWidth));
    var h = Math.max(1, Math.round(box.height || canvas.offsetHeight));
    if (w < 2 || h < 2) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = w * dpr;
    canvas.height = h * dpr;

    var g = canvas.getContext("2d");
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    g.lineCap = "round";
    g.lineJoin = "round";

    var data = sampleField(w, h, o);
    var span = data.max - data.min || 1;

    for (var n = 1; n < o.levels; n++) {
      var level = data.min + (span * n) / o.levels;
      var isIndex = n % o.indexEvery === 0;
      g.strokeStyle = isIndex ? o.strokeIndex : o.stroke;
      g.lineWidth = isIndex ? o.widthIndex : o.width;
      traceLevel(g, data, level, o.cell);
    }
  }

  /* Draw now, and again after a resize settles. Returns nothing; the canvas
     is the output. */
  function mount(canvas, options) {
    draw(canvas, options);
    var t;
    window.addEventListener("resize", function () {
      clearTimeout(t);
      t = setTimeout(function () {
        draw(canvas, options);
      }, 120);
    });
  }

  window.AtlasContours = { draw: draw, mount: mount };
})();
